import React from 'react';
import { TestimonialsSection } from '@/components/home/TestimonialsSection';
import { logAnalyticsEvent, ANALYTICS_EVENTS } from '@/utils/Analytics';
import { publicAsset } from '@/utils/publicAsset';
import { appHref } from '@/utils/appHref';

/**
 * The value-first homepage body (below the diagnostic hero). Angle A — "Full
 * Value", with Pam's credibility pulled up near the top.
 *
 * Grounded in the .pen design language (see the design protocol): hard "ledge"
 * shadows (no gradients), rounded-2xl border-2 surfaces, brand teal (primary-*)
 * with a plum "Pam" surface and gold accents, Fraunces via `font-display`.
 *
 * Why this exists: the homepage's job is to convert cold visitors who scroll
 * past the free diagnostic. The competitive teardown (2026-08-18) found the
 * category leads with "more questions"; the unclaimed ground is (a) an instant
 * personalized readiness score, (b) a real, named practicing LCSW behind every
 * question, (c) one-time pricing vs the subscription treadmill, (d) AI clinical
 * sims. Every claim here is true — Pam's credentials are her actual, verifiable
 * record (University of Michigan MSW, LCSW), never invented.
 */

// Pam Coin's real, LinkedIn-verified credential record. Do not embellish.
const PAM = {
  name: 'Pamela Coin, MSW, LCSW',
  role: 'Co-founder & clinical reviewer · Board Approved Clinical Supervisor',
  school: 'University of Michigan',
  degree: 'Master of Social Work (MSW)',
  creds: ['LCSW', 'Board Approved Clinical Supervisor', 'ACSW', 'CCM'],
  email: 'pam@socialworktestprepacademy.com',
  practiceUrl: 'https://www.pcmilestones.com/',
  practiceLabel: 'PC Milestones',
  photo: publicAsset('pamela-image.png'), // the app's canonical Pam headshot (see PamByline)
};

const ledge = (rgba: string): React.CSSProperties => ({ boxShadow: `0 6px 0 ${rgba}` });
const NEUTRAL_LEDGE = ledge('rgba(17,17,17,0.06)');

const Eyebrow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="text-xs font-bold uppercase tracking-wider text-primary-600 bg-primary-50 px-4 py-2 rounded-full inline-block mb-4">
    {children}
  </span>
);

const SectionHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h2 className="text-2xl md:text-3xl font-display font-bold text-neutral-900 leading-tight mb-3">
    {children}
  </h2>
);

// ── 1. How it works ──────────────────────────────────────────────────────────
export type LandingVariant = 'six_turn' | 'classic';

// The six turns give no score, no weak-area report and no emailed Pass Plan, so that variant never claims them.
const HowItWorks: React.FC<{ variant: LandingVariant }> = ({ variant }) => {
  const firstSteps =
    variant === 'six_turn'
      ? [
          { n: 1, t: 'Try it free', d: 'Six real moments: a question that adapts, a clinical simulation, a timed question, a flashcard, and a question for Pam.' },
          { n: 2, t: 'Keep going, free', d: 'Save it to a free account and keep going: 7 days of everything, no card.' },
        ]
      : [
          { n: 1, t: 'Take the free diagnostic', d: '6 real questions in the 2026 format. Three minutes, no account, no card. Enter your email at the end and your readiness score and Pass Plan are yours.' },
          { n: 2, t: 'See exactly where you stand', d: 'Not just a number: the specific content areas you are weakest in, so you know the exact thing to study next.' },
        ];
  const steps = [
    ...firstSteps,
    { n: 3, t: 'Close the gap, until you pass', d: 'Adaptive practice that adjusts to what you miss, every question reviewed by an LCSW. One payment, and we stay with you until you pass.' },
  ];
  return (
    <section className="mt-12 text-center">
      <Eyebrow>How it works</Eyebrow>
      <SectionHeading>
        {variant === 'six_turn' ? <>From a free try to &ldquo;I passed.&rdquo;</> : <>From &ldquo;where do I stand?&rdquo; to &ldquo;I passed.&rdquo;</>}
      </SectionHeading>
      <div className="grid gap-4 mt-6 sm:grid-cols-3 text-left">
        {steps.map((s) => (
          <div key={s.n} className="rounded-2xl border-2 border-neutral-200 bg-white p-5" style={NEUTRAL_LEDGE}>
            <div className="w-9 h-9 rounded-xl bg-primary-600 text-white font-display font-bold flex items-center justify-center mb-3" style={ledge('#1B3A38')}>
              {s.n}
            </div>
            <h3 className="font-bold text-neutral-900 mb-1">{s.t}</h3>
            <p className="text-sm text-neutral-600 leading-relaxed">{s.d}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

// ── 2. What your readiness score means ───────────────────────────────────────
const ReadinessMeaning: React.FC = () => (
  <section className="mt-14">
    <div className="rounded-2xl border-2 border-neutral-200 bg-white p-6 md:p-8" style={NEUTRAL_LEDGE}>
      <Eyebrow>Your readiness score</Eyebrow>
      <SectionHeading>A number that tells the truth, not a pep talk.</SectionHeading>
      <p className="text-neutral-600 leading-relaxed mb-6">
        Most prep hands you a pile of questions and hopes. Your readiness score estimates how close you
        are to exam-ready <em>right now</em>, and updates as you practice, so you spend your time on the
        20% that actually moves the needle instead of grinding through what you already know.
      </p>
      {/* 0-100 readiness scale — a simple, on-brand instrument, not a stock chart. */}
      <div className="relative h-3 rounded-full bg-neutral-100 border border-neutral-200">
        <div className="absolute inset-y-0 left-0 rounded-full bg-primary-600" style={{ width: '72%' }} />
        <div className="absolute -top-1.5 h-6 w-6 rounded-full bg-white border-2 border-primary-600 flex items-center justify-center" style={{ left: 'calc(72% - 12px)', boxShadow: '0 3px 0 rgba(27,58,56,0.15)' }}>
          <span className="w-1.5 h-1.5 rounded-full bg-primary-600" />
        </div>
      </div>
      <div className="flex justify-between text-xs font-semibold text-neutral-400 mt-2">
        <span>Not yet</span>
        <span className="text-primary-700">Your number, updated as you go</span>
        <span>Exam ready</span>
      </div>
    </div>
  </section>
);

// ── 3. Pam (credibility, pulled up) ──────────────────────────────────────────
const PamCredibility: React.FC = () => (
  <section className="mt-14">
    {/* Plum "Pam" surface, mirroring the .pen "Pam's Tip" treatment. */}
    <div
      className="rounded-2xl border-2 p-6 md:p-8"
      style={{ backgroundColor: '#F6EEF3', borderColor: '#E3CFDC', ...ledge('rgba(139,90,124,0.18)') }}
    >
      <Eyebrow>Every question, reviewed by a clinician</Eyebrow>
      <SectionHeading>You&rsquo;re studying an LCSW&rsquo;s judgment, not content-farm filler.</SectionHeading>
      <p className="text-neutral-700 leading-relaxed mb-6">
        {PAM.name.split(',')[0]} is a practicing psychotherapist in Washington, DC, a licensed
        clinical social worker and Board Approved Clinical Supervisor whose work spans community
        mental health, healthcare, and private practice. She reviews SWTPA&rsquo;s
        questions and explanations for clinical soundness, so what you study reflects how the work
        actually gets done, and how the exam actually tests it.
      </p>

      {/* Clean credential block — University of Michigan in its maize/blue, no trademarked mark. */}
      <div className="rounded-xl border-2 border-neutral-200 bg-white p-5" style={NEUTRAL_LEDGE}>
        <div className="flex items-center gap-4">
          <img
            src={PAM.photo}
            alt={PAM.name}
            width={64}
            height={64}
            className="flex-shrink-0 w-16 h-16 rounded-full object-cover border-2 border-white"
            style={{ boxShadow: '0 3px 0 rgba(139,90,124,0.25)' }}
          />
          <div>
            <div className="font-display font-bold text-neutral-900 text-lg leading-tight">{PAM.name}</div>
            <div className="text-sm text-neutral-500">{PAM.role}</div>
            <div className="text-sm font-semibold mt-0.5 flex items-center gap-1.5 flex-wrap">
              <span
                className="inline-flex items-center justify-center w-5 h-5 rounded font-display font-bold text-[11px]"
                style={{ backgroundColor: '#00274C', color: '#FFCB05' }}
                aria-hidden="true"
              >
                M
              </span>
              <span style={{ color: '#00274C' }}>{PAM.school} &middot; {PAM.degree}</span>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          {PAM.creds.map((c) => (
            <span key={c} className="text-xs font-bold text-neutral-700 bg-neutral-100 border border-neutral-200 rounded-full px-3 py-1">
              {c}
            </span>
          ))}
        </div>
        <div className="flex flex-wrap gap-x-5 gap-y-1 mt-4 text-sm">
          <a href={PAM.practiceUrl} target="_blank" rel="noopener noreferrer" className="font-semibold text-primary-700 hover:text-primary-800 underline">
            {PAM.practiceLabel} &#8599;
          </a>
          <a href={`mailto:${PAM.email}`} className="font-semibold text-primary-700 hover:text-primary-800 underline">
            {PAM.email}
          </a>
        </div>
      </div>
    </div>
  </section>
);

// ── 4. Adaptive engine + AI sims ─────────────────────────────────────────────
const WhatMakesItDifferent: React.FC = () => {
  const cards = [
    { t: 'Prep that adapts to you', d: 'The question bank adjusts to your level: harder where you’re strong, focused where you’re weak. You stop re-answering what you already know.' },
    { t: 'AI clinical simulations', d: 'Practice the real decisions the exam is built to test: interactive clinical scenarios where you make the call, not just pick a letter. Nothing else in ASWB prep does this.' },
  ];
  return (
    <section className="mt-14 text-center">
      <Eyebrow>Not a pile of questions</Eyebrow>
      <SectionHeading>Built to change your score, not just your streak.</SectionHeading>
      <div className="grid gap-4 mt-6 sm:grid-cols-2 text-left">
        {cards.map((c) => (
          <div key={c.t} className="rounded-2xl border-2 border-neutral-200 bg-white p-6" style={NEUTRAL_LEDGE}>
            <h3 className="font-display font-bold text-neutral-900 text-lg mb-2">{c.t}</h3>
            <p className="text-sm text-neutral-600 leading-relaxed">{c.d}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

// ── 5. Pricing: one-time vs subscription ─────────────────────────────────────
// A distinct `source` per door so GA4 can rank which entry point actually earns
// the click (subscription_clicked was firing 0 times because no door existed).
const onPricingClick = () => {
  logAnalyticsEvent(ANALYTICS_EVENTS.SUBSCRIPTION_CLICKED, {
    subscriptionStatus: null,
    isLoggedIn: false,
    source: 'home_pricing_block',
    timestamp: Date.now(),
  });
};

const PricingContrast: React.FC = () => (
  <section className="mt-14">
    <div className="rounded-2xl border-2 border-primary-200 bg-primary-50 p-6 md:p-8 text-center" style={ledge('rgba(27,58,56,0.10)')}>
      <Eyebrow>Pricing</Eyebrow>
      <SectionHeading>Pay once. No subscription. We&rsquo;re with you until you pass.</SectionHeading>
      <p className="text-neutral-600 leading-relaxed max-w-xl mx-auto mb-6">
        Most prep bills you every month until you give up. SWTPA is <strong>$129, one time</strong> for
        full access, and if you don&rsquo;t pass, you keep going at no extra charge.
      </p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center text-left max-w-lg mx-auto">
        {/* The $129 tile was pure copy with no link — the homepage stated the
            offer and gave no way to take it. It is now the CTA itself. */}
        <a
          href={appHref('/pricing')}
          data-testid="home-pricing-cta"
          onClick={onPricingClick}
          className="flex-1 block rounded-xl border-2 border-primary-600 bg-white p-4 hover:bg-primary-50 transition-colors"
          style={ledge('#1B3A38')}
        >
          <div className="font-display font-bold text-neutral-900 text-lg">$129 once</div>
          <div className="text-sm text-neutral-600">Full access, until you pass. No renewals.</div>
          <div className="mt-3 inline-flex items-center gap-1 font-display font-bold text-primary-700 text-sm">
            See what&rsquo;s included <span aria-hidden="true">&rarr;</span>
          </div>
        </a>
        <div className="flex-1 rounded-xl border-2 border-neutral-200 bg-white/60 p-4">
          <div className="font-display font-bold text-neutral-400 text-lg line-through">$20&ndash;50 / month</div>
          <div className="text-sm text-neutral-400">Everyone else, billing until you quit.</div>
        </div>
      </div>

      <a
        href={appHref('/pricing')}
        data-testid="home-pricing-button"
        onClick={onPricingClick}
        className="inline-block mt-6 px-6 py-3.5 rounded-xl bg-primary-600 hover:bg-primary-700 text-white font-display font-bold text-lg transition-colors"
        style={ledge('#1B3A38')}
      >
        Get Full Access for $129
      </a>
    </div>
  </section>
);

// ── 7. FAQ ───────────────────────────────────────────────────────────────────
const FAQ: React.FC<{ variant: LandingVariant }> = ({ variant }) => {
  const sixTurn = variant === 'six_turn';
  const qs = [
    {
      q: 'Is the diagnostic really free?',
      a: sixTurn
        ? 'Six short turns: a question that adapts to your answer, a clinical simulation moment, a timed question, a flashcard, and a question for Pam. Free, no account to start.'
        : 'Yes. Six questions, three minutes, no account and no credit card. At the end you enter your email: we show your readiness score on the spot and send your Pass Plan. That is the only thing we ask for.',
    },
    { q: 'What is the 2026 format?', a: 'The ASWB moved to three content areas and three answer choices per question. Our questions match it.' },
    { q: 'Which exam does it cover?', a: sixTurn ? 'LBSW, LMSW, and LCSW.' : 'LBSW, LMSW, and LCSW. You pick yours before the diagnostic starts.' },
    { q: 'What does "until you pass" mean?', a: 'One payment of $129 for full access. If you don’t pass, you keep going at no extra charge.' },
    { q: 'Who is behind the questions?', a: 'Every question is reviewed by Pamela Coin, LCSW (University of Michigan MSW), a practicing psychotherapist. See her credentials above.' },
  ];
  return (
    <section className="mt-14">
      <div className="text-center">
        <Eyebrow>Questions</Eyebrow>
        <SectionHeading>Before you start</SectionHeading>
      </div>
      <div className="mt-6 space-y-3">
        {qs.map((item) => (
          <div key={item.q} className="rounded-xl border-2 border-neutral-200 bg-white p-5" style={NEUTRAL_LEDGE}>
            <h3 className="font-bold text-neutral-900 mb-1">{item.q}</h3>
            <p className="text-sm text-neutral-600 leading-relaxed">{item.a}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

// ── Closing CTA — back to the diagnostic at the top of this page ──────────────
const ClosingCTA: React.FC<{ variant: LandingVariant }> = ({ variant }) => (
  <section className="mt-14 mb-4 text-center">
    <button
      type="button"
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      className="px-6 py-3.5 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-bold text-lg transition-colors"
      style={ledge('#1B3A38')}
    >
      {variant === 'six_turn' ? <>Try the six turns &uarr;</> : <>See where you stand: take the free diagnostic &uarr;</>}
    </button>
    <p className="text-xs text-neutral-400 mt-3">
      {variant === 'six_turn' ? 'Free' : '3 minutes'} &middot; no account &middot; reviewed by a practicing LCSW
    </p>
  </section>
);

export const LandingValueSections: React.FC<{ variant?: LandingVariant }> = ({ variant = 'classic' }) => (
  <div className="animate-fade-in" data-testid="landing-value-sections">
    <HowItWorks variant={variant} />
    {variant === 'classic' && <ReadinessMeaning />}
    <PamCredibility />
    <WhatMakesItDifferent />
    <PricingContrast />
    <TestimonialsSection />
    <FAQ variant={variant} />
    <ClosingCTA variant={variant} />
  </div>
);

export default LandingValueSections;
